/* Customer reviews slider.
   Scrolls one review at a time, stops at both ends, and keeps a progress bar
   in sync with the track. Touch and trackpad scrolling drive the same state. */

if (!customElements.get('pcr-slider')) {
  class PcrSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-pcr-track]');
      if (!this.track) return;

      this.prevButton = this.querySelector('[data-pcr-prev]');
      this.nextButton = this.querySelector('[data-pcr-next]');
      this.progress = this.querySelector('[data-pcr-progress]');
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.onScroll = this.onScroll.bind(this);
      this.sync = this.sync.bind(this);

      if (this.prevButton) this.prevButton.addEventListener('click', () => this.step(-1));
      if (this.nextButton) this.nextButton.addEventListener('click', () => this.step(1));

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.sync);

      this.setupAutoplay();
      this.sync();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.sync);
      this.stopAutoplay();
    }

    get slides() {
      return Array.from(this.track.children);
    }

    // Distance from one review to the next, gap included.
    get slideStep() {
      const slides = this.slides;
      if (slides.length > 1) return slides[1].offsetLeft - slides[0].offsetLeft;
      return slides.length ? slides[0].offsetWidth : 0;
    }

    get maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    step(direction) {
      const distance = this.slideStep;
      if (!distance) return;
      this.track.scrollBy({ left: direction * distance, behavior: this.reduced ? 'auto' : 'smooth' });
    }

    onScroll() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(this.sync);
    }

    sync() {
      const max = this.maxScroll;
      const position = this.track.scrollLeft;

      if (this.prevButton) this.prevButton.disabled = position <= 1;
      if (this.nextButton) this.nextButton.disabled = position >= max - 1;

      if (this.progress) {
        this.progress.hidden = max <= 1;
        const visible = max > 0 ? this.track.clientWidth / this.track.scrollWidth : 1;
        const travelled = max > 0 ? position / max : 0;
        this.progress.style.setProperty('--pcr-progress-width', (visible * 100).toFixed(2) + '%');
        this.progress.style.setProperty('--pcr-progress-offset', (travelled * (100 - visible * 100)).toFixed(2) + '%');
      }
    }

    setupAutoplay() {
      const delay = parseInt(this.dataset.autoplayDelay, 10);
      if (this.dataset.autoplay !== 'true' || this.reduced || !delay) return;

      this.autoplayDelay = delay;
      this.startAutoplay();

      ['mouseenter', 'focusin', 'touchstart'].forEach((event) =>
        this.addEventListener(event, () => this.stopAutoplay(), { passive: true })
      );
      ['mouseleave', 'focusout'].forEach((event) =>
        this.addEventListener(event, () => this.startAutoplay())
      );
    }

    startAutoplay() {
      if (!this.autoplayDelay || this.timer) return;
      this.timer = setInterval(() => {
        if (this.track.scrollLeft >= this.maxScroll - 1) {
          this.track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this.step(1);
        }
      }, this.autoplayDelay);
    }

    stopAutoplay() {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  customElements.define('pcr-slider', PcrSlider);
}
